// Copyright ©️ 2026 eVolve MEP, LLC

import { type FocusEvent, type ReactNode, useCallback, useId } from 'react';

import { Box, Input, UnstyledButton } from '@mantine/core';

import { parseMeasurement } from 'components/Mantine/InchesInput';
import classes from 'components/Mantine/StepperInput.module.css';
import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useCommittedNumberInput } from 'hooks/useCommittedNumberInput';

const SIXTEENTH = 1 / 16;

type InchesModeProps = {
  /** Lengths, typed and shown as fractional inches rounded to a sixteenth. */
  mode: 'inches';
  /**
   * If `true`, `value`, `min`, `max` and `step` are decimal feet, while the field still shows inches.
   * @defaultValue false
   */
  valueInFeet?: boolean;
  suffix?: never;
};

type CountModeProps = {
  /** Whole numbers, such as a quantity. */
  mode: 'count';
  valueInFeet?: never;
  /** Text shown after the number, such as `" ea"`. Typing it is optional. */
  suffix?: string;
};

export type StepperInputProps = {
  'value'?: number | null;
  'onChange': (value: number | null) => void;
  /** Smallest allowed value. Typed and stepped values are clamped to it. In the same unit as `value`. */
  'min'?: number;
  /** Largest allowed value. Typed and stepped values are clamped to it. In the same unit as `value`. */
  'max'?: number;
  /** Amount each − / + tap changes the value by. In the same unit as `value`. */
  'step': number;
  'label'?: ReactNode;
  /** Accessible name for the field when there is no visible `label`. */
  'aria-label'?: string;
  'description'?: ReactNode;
  'error'?: ReactNode;
  'required'?: boolean;
  'disabled'?: boolean;
  /** Shows the value but blocks typing and the − / + buttons. */
  'readOnly'?: boolean;
  /**
   * When true, the stepper is 40px tall and 122px wide, for tight spaces such as a list row.
   * @defaultValue false
   */
  'compact'?: boolean;
  /**
   * When true, clearing the field sets the value to `null`. When false, a cleared field reverts.
   * @defaultValue false
   */
  'allowBlank'?: boolean;
  'placeholder'?: string;
  'autoFocus'?: boolean;
  /** Called after Enter commits a typed value. */
  'onSubmit'?: () => void;
  'onFocus'?: (event: FocusEvent<HTMLInputElement>) => void;
  'onBlur'?: (event: FocusEvent<HTMLInputElement>) => void;
  'onMouseEnter'?: () => void;
  'onMouseLeave'?: () => void;
} & (InchesModeProps | CountModeProps);

/**
 * A touch-sized number input: a − button, a typed field, and a + button, each 56px tall.
 *
 * Built for the Bend Creator on shop tablets, where users wear gloves. `onChange` only fires when a value is
 * committed: on Enter, on leaving the field, or on a − / + tap. A − / + tap straight after typing applies the typed
 * value first, then steps it.
 *
 * - `mode="inches"` accepts `14`, `14.5`, `14 1/2`, `14-1/2` and `1/2`, rounds to a sixteenth, and opens the full
 *   keyboard so a fraction can be typed.
 * - `mode="count"` accepts whole numbers with or without the suffix, and opens the number pad.
 */
export const StepperInput = ({
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step,
  label,
  'aria-label': ariaLabel,
  description,
  error,
  required,
  disabled,
  readOnly,
  compact = false,
  allowBlank = false,
  placeholder,
  autoFocus,
  onSubmit,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  mode,
  valueInFeet = false,
  suffix = '',
}: StepperInputProps) => {
  const inchesPerUnit = mode === 'inches' && valueInFeet ? 12 : 1;

  const normalize = useCallback(
    (raw: number) => {
      const rounded =
        mode === 'inches'
          ? (Math.round((raw * inchesPerUnit) / SIXTEENTH) * SIXTEENTH) / inchesPerUnit
          : Math.round(raw);
      return Math.min(max, Math.max(min, rounded));
    },
    [mode, inchesPerUnit, min, max],
  );

  const format = useCallback(
    (committed: number) =>
      mode === 'inches'
        ? convertInchesToImperialString(committed * inchesPerUnit)
        : `${formatQuantity(committed)}${suffix}`,
    [mode, inchesPerUnit, suffix],
  );

  const parse = useCallback(
    (text: string) => {
      if (mode === 'inches') {
        // No tolerance, so any fraction or decimal is read; normalize rounds it to a sixteenth.
        const inches = parseMeasurement(text, false, 'fraction');
        return isNil(inches) ? null : normalize(inches / inchesPerUnit);
      }
      const count = parseFloat(text.replace(suffix.trim(), '').replaceAll(/[^\d.-]/g, ''));
      return Number.isFinite(count) ? normalize(count) : null;
    },
    [mode, inchesPerUnit, suffix, normalize],
  );

  const { inputProps, commitDraft } = useCommittedNumberInput({
    value,
    format,
    parse,
    onCommit: onChange,
    allowBlank,
    onFocus,
    onBlur,
    onSubmit,
  });

  const id = useId();
  const locked = disabled || readOnly;
  const stepBy = (direction: 1 | -1) => {
    const base = commitDraft() ?? (Number.isFinite(min) ? min : 0);
    const next = normalize(base + direction * step);
    if (next !== base) onChange(next);
  };

  const atMin = isNotNil(value) && value <= min;
  const atMax = isNotNil(value) && value >= max;
  const hasError = isNotNil(error) && error !== false;

  return (
    <Input.Wrapper
      id={id}
      label={label}
      description={description}
      error={error}
      required={required}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box
        className={classes.stepper}
        data-error={hasError || undefined}
        data-disabled={locked || undefined}
        data-compact={compact || undefined}
      >
        <UnstyledButton
          className={classes.stepButton}
          onClick={() => stepBy(-1)}
          disabled={locked || atMin}
          aria-label="Decrease"
        >
          −
        </UnstyledButton>
        <Box
          component="input"
          id={id}
          aria-label={ariaLabel}
          className={classes.field}
          {...inputProps}
          inputMode={mode === 'inches' ? 'text' : 'numeric'}
          enterKeyHint="done"
          autoComplete="off"
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          readOnly={readOnly}
        />
        <UnstyledButton
          className={classes.stepButton}
          onClick={() => stepBy(1)}
          disabled={locked || atMax}
          aria-label="Increase"
        >
          +
        </UnstyledButton>
      </Box>
    </Input.Wrapper>
  );
};
